// Shared handler for the FormEvo "token expired" response (HTTP 498).
//
// FormEvo proxies calls using the user's stored FormEvo access token. When that token
// has expired, the backend returns 498 (distinct from the Osprey session 401). When that
// happens we prompt the user to reconnect to FormEvo and, on confirmation, send them to
// the FormEvo setup page to re-authenticate.

// Path to the FormEvo setup page (the app uses HashRouter, so we navigate via the hash,
// the same way redirectToLogout does in ospreyAPI). The reauthenticate flag tells
// FormEvoSetup the user arrived because their session expired, so it offers a reconnect
// option even though the backend still reports a (stale, expired) token as "connected".
const FORMEVO_AUTH_PATH = '/configuration/third-party-forms?reauthenticate=true'

// Guard so concurrent FormEvo failures don't stack multiple re-auth dialogs.
let isReauthDialogOpen = false

const REAUTH_MESSAGE =
  "Your FormEvo session has expired. You'll need to reconnect to FormEvo to continue. Reconnect now?"

/**
 * Detects a FormEvo token-expiry (498) error and, if found, shows a confirm dialog
 * prompting the user to reconnect to FormEvo.
 *
 * Safe to call on any caught error — it no-ops for non-498 errors.
 *
 * @param {unknown} error - the error caught from an ospreyAPI call
 * @returns {Promise<boolean>} true if the error was a 498 (handled), false otherwise
 */
export const handleFormEvoTokenExpired = async (error) => {
  const status = error?.status || error?.response?.status || error?.cause?.status
  if (status !== 498) return false

  // already prompting for this — don't open a second dialog
  if (isReauthDialogOpen) return true
  isReauthDialogOpen = true

  try {
    // Dynamic import: @repo/ui already depends on @repo/store, so a static import here
    // would create a circular dependency. Importing at call-time (only on a real 498)
    // avoids the cycle.
    const { confirm } = await import('@repo/ui/Confirm')
    const proceed = await confirm(REAUTH_MESSAGE)
    if (proceed) {
      window.location.hash = FORMEVO_AUTH_PATH
    }
  } finally {
    isReauthDialogOpen = false
  }

  return true
}
